import { useState } from 'react';
import { useLang } from '../../../hooks/useLang';

const TECH_NODES = [
  {
    id: 'blockchain-types',
    icon: 'ri-database-2-line',
    color: '#7C3AED',
    title: '链的世界：公链、私链、联盟链',
    subtitle: '三种区块链类型的本质区别与选择逻辑',
    metaphor: {
      title: '形象比喻：三种"账本"的治理哲学',
      content: '公链像一家任何人都可以进、任何人都可以看、任何人都可以参与记账的"天下第一大公证处"，规则刻在石碑上、不可更改、无人主宰——但门口永远排长队、费用高昂。\n\n私链像一家银行的内部核心账务系统，只有内部员工能操作，高效、可控，但和"去中心化"毫无关系，本质上仍是一家公司说了算。\n\n联盟链像由十几家银行共同建的清算中心：入场要资质审批、参与者有限但可互信、规则由成员共同制定——效率高、可监管、但不"完全开放"。'
    },
    detail: [
      {
        name: '公链（Public Chain）',
        tag: '完全去中心化',
        tagColor: '#10B981',
        desc: '任何人都可以参与读写和共识验证的开放区块链。代表：以太坊（Ethereum）、比特币（Bitcoin）、Solana、Polygon。没有任何机构可以修改数据、冻结账户或暂停运行。',
        pros: '完全去中心化、抗审查、全球互通、生态最成熟、ERC标准完整',
        cons: 'TPS低、Gas费波动大、无隐私保护、监管难度高',
        rwaUse: '国际RWA/STO首选：Blackrock BUIDL（以太坊）、Ondo Finance（以太坊+Polygon）、Franklin Templeton（Polygon）',
      },
      {
        name: '私链（Private Chain）',
        tag: '完全中心化',
        tagColor: '#EF4444',
        desc: '完全由单一机构控制的区块链，只有获得授权的节点才能参与。与传统数据库相比优势不大，几乎不用于RWA生产环境。代表：Hyperledger Fabric单机构部署。',
        pros: '极高性能、完全可控、隐私保护强',
        cons: '与传统数据库无本质区别、去中心化价值为零、市场认可度低',
        rwaUse: '极少用于RWA，主要用于企业内部数据管理实验性场景',
      },
      {
        name: '联盟链（Consortium Chain）',
        tag: '许可型·境内主流',
        tagColor: '#F59E0B',
        desc: '由多个机构共同维护的许可型区块链，加入需审核，数据半公开。境内合规路径唯一选择。代表：蚂蚁链、长安链、FISCO BCOS、BSN（区块链服务网络）。',
        pros: '高性能（TPS万级）、可监管、不发代币、境内合规、可存证可溯源',
        cons: '去中心化程度有限、跨境互通难、不适合发行可交易代币',
        rwaUse: '境内供应链金融、贸易融资、资产存证、不动产确权登记',
      },
    ],
    chinaNote: {
      title: '为什么中国RWA既不用公链也不用私链？例外：哪些链被允许了？',
      content: [
        '【不用公链的核心原因】公链的代币（ETH、ERC-20等）本身被十部委定性为"虚拟货币"，在中国境内持有、交易、发行均违法。使用以太坊公链发行RWA代币即等于在境内发行虚拟货币，直接触法。此外，公链的匿名特性、境外节点控制权、无法被执法机构管辖等特性，与中国金融监管的基本原则完全冲突。',
        '【不用私链】私链由单一机构控制，缺乏多方参与的信任背书，且无行业互通标准，无法满足跨机构金融业务的可信要求，监管机构也不认可其存证效力。',
        '【用联盟链】联盟链不发代币（仅做存证）、有备案机制（网信办区块链信息服务备案）、多机构共同记账具有公信力、符合国家监管要求、数据可控。境内RWA的底层确权、存证、融资流转均走联盟链。',
        '【例外！Conflux（树图网络）——中国唯一政府支持的合规公链】Conflux是由清华大学姚期智院士团队研究成果转化、获上海市政府支持的特殊公链。其特殊性在于：①由国内机构控制节点比例；②与人民银行数字货币研究所有合作；③可与数字人民币（e-CNY）集成。已参与上海数字人民币NFT试验、香港跨境RWA测试等项目。但即便如此，Conflux用于境内RWA仍需监管许可，不能自行发行代币。',
        '【例外！HashKey Chain——SFC持牌机构的以太坊L2】HashKey Chain是由SFC持牌机构HashKey Group运营的以太坊Layer2公链，部署于香港合规环境，内置KYC合规模块、合规白名单合约，专为香港RWA代币发行设计。由于在境外（香港）运营，并由持牌机构管控，其使用不违反内地规定，但仅供境外RWA发行，内地企业须通过香港SPV在此发行代币。',
      ],
    },
  },
  {
    id: 'hash',
    icon: 'ri-fingerprint-line',
    color: '#EF4444',
    title: '哈希值：区块链的"数字指纹"',
    subtitle: '每笔交易和每个区块的唯一身份标识符',
    metaphor: {
      title: '形象比喻：无法伪造的"DNA指纹鉴定"',
      content: '哈希函数就像一台神奇的"指纹机"：你可以把任意一段内容（一篇文章、一张图片、一个RWA合同）放进去，机器会输出一串固定长度的字符（如256位），这串字符就是该内容的"数字指纹"。\n\n神奇的地方在于三个特性：\n①单向性——知道"指纹"，无法反推出原始内容（就像知道DNA结果，无法合成出完整人体）；\n②唯一性——哪怕只改一个字，指纹就完全不同（"我爱RWA"和"我爱RWA." 的哈希值天壤之别）；\n③快速性——再复杂的内容，计算出指纹只需毫秒。\n\n在RWA中，哈希值有多重用途：每个区块的哈希值包含上一个区块的哈希值，从而形成"链"——如果有人篡改历史区块，其哈希值就会变化，导致之后所有区块的哈希值全部失效，立刻被发现。这就是"不可篡改"的数学原理。'
    },
    detail: [
      {
        name: '哈希函数与SHA-256',
        tag: '核心技术',
        tagColor: '#EF4444',
        desc: 'SHA-256是比特币和许多区块链使用的哈希算法，将任意长度输入转化为256位（32字节）的固定输出。以太坊使用Keccak-256算法。一个以太坊地址就是公钥经Keccak-256哈希后取后20字节生成的，确保地址唯一且不可逆推公钥。',
        pros: '计算极快、不可用于加密（单向，不可解密）、极小概率存在"碰撞"（两个不同输入得到相同哈希）',
        cons: '无法用于加密（单向，不可解密）、极小概率存在"碰撞"（两个不同输入得到相同哈希）',
        rwaUse: 'RWA合同文件的链上存证：将合同PDF的SHA-256哈希值上链，任何人可对比哈希值验证合同是否被篡改，无需上链原文，保护隐私的同时实现存证',
      },
      {
        name: '区块哈希与链式结构',
        tag: '防篡改机制',
        tagColor: '#7C3AED',
        desc: '每个区块包含：①本区块所有交易的哈希树根（Merkle Root）；②上一个区块的哈希值（Previous Hash）；③时间戳和随机数（Nonce）。这三者共同计算出本区块的哈希值。改动历史任何交易都会改变Merkle Root，进而改变区块哈希，进而破坏后续所有区块——篡改在数学上被证明为不可能。',
        pros: '历史不可篡改（修改任何交易需重新计算所有后续区块）、完整性可验证',
        cons: '验证链的完整性需要完整节点数据（约几百GB）',
        rwaUse: '香港RWA平台利用区块链哈希结构向SFC证明：代币化资产的发行时间、持有人变化、分红历史均无法被事后篡改',
      },
    ],
  },
  {
    id: 'merkle',
    icon: 'ri-node-tree',
    color: '#F59E0B',
    title: '默克尔树：高效验证的数学结构',
    subtitle: '如何在不下载完整数据的情况下验证某笔交易的存在',
    metaphor: {
      title: '形象比喻：只需一张"证明纸"就能证明你的交易存在',
      content: '想象一棵倒置的树：树上有10000片叶子，每片叶子是一笔交易的哈希值。相邻的两片叶子合并成一个"树枝节点"（两个哈希再哈希），树枝再合并，最终汇聚到一个"树根"（Merkle Root）。\n\n这个Merkle Root被存在区块头中，代表了这个区块所有10000笔交易的"摘要"。\n\n神奇之处：如果你想证明"你的RWA认购交易确实被包含在这个区块中"，不需要下载所有10000笔交易——只需要一条从你的交易到根节点的"证明路径"（约13个哈希值）。通过这13个哈希值的推算，可以还原出Merkle Root，与链上的Root对比即可验证。\n\n这就是"Merkle Proof（默克尔证明）"——是轻节点（手机钱包）验证交易的数学基础，也是Layer2和跨链协议的核心工具。'
    },
    detail: [
      {
        name: '默克尔树与轻节点验证',
        tag: '高效验证',
        tagColor: '#F59E0B',
        desc: 'Merkle Tree使手机/平板等轻量级客户端无需下载完整区块链（400GB+），只需下载区块头（80字节/块）即可验证特定交易是否存在。比特币的SPV（简化验证）和以太坊的Merkle Patricia Trie都基于此原理。',
        pros: '验证效率极高（O(log n)复杂度）、轻节点可用、支持零知识证明集成',
        cons: '仅能证明"交易存在"，无法证明"交易没发生"；完整历史验证仍需全节点',
        rwaUse: 'Layer2的RWA代币交易通过Merkle Proof提交到以太坊主链，主链只验证Root而非所有交易，大幅降低Gas费',
      },
    ],
  },
  {
    id: 'keypair',
    icon: 'ri-key-2-line',
    color: '#10B981',
    title: '公私钥：你在区块链上的"身份证+印章"',
    subtitle: '私钥是绝对所有权，公钥是公开地址——失去私钥等于失去一切',
    metaphor: {
      title: '形象比喻：永远没有备份的"宇宙唯一密码箱"',
      content: '区块链上的身份系统基于非对称加密：私钥和公钥是一对数学上相关联的数字，但只能由私钥推导公钥，无法反向操作。\n\n私钥就是你的"私章+密码"合二为一：你用私钥"签名"一笔交易（证明你同意支出），任何人都可以用对应的公钥验证签名是真实的——但无法从公钥推出私钥来伪造你的签名。\n\n最深刻的启示：在银行里，你忘了密码可以挂失、可以凭身份证重置。但在区块链上，私钥是纯数学生成的——没有银行、没有政府、没有任何人能帮你"找回"私钥。如果你的私钥刻在一块木头上被烧掉了，那块木头上的所有RWA代币就永远消失了，即使全人类加上所有计算机都无法找回。\n\n这就是为什么RWA合规框架要求"独立托管"——让专业机构保管私钥，而不是发行人或投资者自己保管。'
    },
    detail: [
      {
        name: '私钥、公钥与钱包地址的关系',
        tag: '身份基础',
        tagColor: '#10B981',
        desc: '私钥（256位随机数）→【ECDSA椭圆曲线算法】→ 公钥（512位）→【Keccak-256哈希+取后20字节】→ 以太坊地址（40位十六进制，如0x742d...）。三者的关系：私钥生成公钥，公钥生成地址；单向不可逆，地址可公开，私钥绝不可泄露。',
        pros: '非对称加密保证安全：只有私钥持有者才能授权交易，任何人都可验证签名真实性',
        cons: '私钥丢失/泄露不可恢复；助记词（Mnemonic）是私钥的备份，同样需要极度保密',
        rwaUse: 'RWA持牌托管机构（如HashKey Custody）的核心价值：用HSM硬件安全模块保管私钥，防止私钥被盗。SFC明确要求RWA代币私钥须由独立托管机构保管。',
      },
      {
        name: '多重签名（MultiSig）',
        tag: 'RWA必备',
        tagColor: '#7C3AED',
        desc: '多签钱包要求多把私钥共同签名才能授权交易（如"3/5多签"：5个密钥持有者中至少3个签名才可转账）。香港SFC要求RWA托管机构对大额代币转移使用多签机制，防止单个内部人员盗取资产。',
        pros: '防止单点失效（1个私钥被盗不够用）、机构内部权力制衡、防范内部欺诈',
        cons: '所有签名者都须在线协调（紧急情况下可能延迟）、多签合约的安全性取决于实现质量',
        rwaUse: 'Gnosis Safe多签是RWA平台最常用的机构钱包；SFC要求大额（如>100万港元）代币转移须多签授权',
      },
    ],
  },
  {
    id: 'smart-contract',
    icon: 'ri-code-s-slash-line',
    color: '#3B82F6',
    title: '智能合约：区块链的"自动法官"',
    subtitle: '预设条件自动履约，无需人工干预',
    metaphor: {
      title: '形象比喻：不需要律师、银行和法院的自动执行合同',
      content: '想象一台老式弹珠机：你投入硬币（触发条件），机器自动弹出弹珠（执行结果）——没有店员，没有信任问题，没有"老板今天不在"的烦恼。\n\n智能合约就是这台机器。代码就是合同条款，区块链就是公证处，"触发→执行"自动完成。更精妙的比喻是：传统合同是"你违约我去法院告你"，智能合约是"你不付钱门永远开不了"——违约在物理上不可能发生。\n\n但注意：代码写错了，"门永远开不了"就真的永远开不了——Ronin Network 6.25亿美元被盗事件的本质就是这台机器的零件被恶意替换。'
    },
    detail: [
      {
        name: '智能合约的工作机制',
        tag: '核心原理',
        tagColor: '#3B82F6',
        desc: '智能合约是部署在区块链上的一段代码，具有唯一地址。当外部账户向其发起交易（调用函数）且满足预设条件时，自动执行并记录结果。一旦部署，任何人（包括开发者）都无法单方面修改。',
        pros: '自动执行（无人工干预）、透明可查（代码开源）、不可篡改（历史不可修改）、可组合（与其他合约交互）',
        cons: '代码Bug不可逆（损失无法追回）、计算成本高（Gas费）、无法主动获取外部数据（需预言机辅助）',
        rwaUse: 'RWA自动分红、KYC白名单管理、代币转让限制（仅限合格投资者）、赎回自动计算、NAV自动更新',
      },
      {
        name: '合约安全审计（必须项）',
        tag: '合规要求',
        tagColor: '#EF4444',
        desc: '智能合约部署前须经独立第三方安全审计，检查重入攻击、整数溢出、访问控制漏洞、逻辑错误等已知风险类型。SFC要求证券型代币合约审计报告须向投资者公开。',
        pros: '发现漏洞（防止被攻击）、提高投资者信心、满足SFC合规要求',
        cons: '审计成本高（CertiK审计费10-30万美元）、审计无法保证100%安全',
        rwaUse: '香港SFC要求证券型RWA代币的智能合约须提供经认可审计机构的安全报告',
      },
    ],
  },
  {
    id: 'evm',
    icon: 'ri-cpu-line',
    color: '#8B5CF6',
    title: 'EVM：以太坊的"通用计算引擎"',
    subtitle: '以太坊虚拟机——所有智能合约的运行环境',
    metaphor: {
      title: '形象比喻：以太坊是一台"全球共享的超级计算机"',
      content: '普通电脑有CPU，以太坊有EVM（以太坊虚拟机）。你的智能合约代码就是"软件"，EVM就是"操作系统"，把你的代码翻译成每台节点电脑都能执行的指令。\n\n更深刻的类比：EVM像1990年代的Java虚拟机（JVM）——"一次编写，到处运行"。你用Solidity写的合约，不管跑在美国的节点还是新加坡的节点，执行结果完全相同。这种"确定性"是区块链信任的技术基础。\n\nEVM的"天才之处"在于：它让Polygon、Avalanche、Optimism、BSC都可以"克隆以太坊的规则"，ERC标准就能跨链通用——就像Java程序可以跑在Windows也可以跑在Linux。'
    },
    detail: [
      {
        name: 'EVM兼容链',
        tag: '跨链基础',
        tagColor: '#8B5CF6',
        desc: 'EVM兼容链是实现了以太坊虚拟机规范的其他公链，可直接运行以太坊合约代码。主要EVM链：Polygon（低Gas）、Arbitrum（高速L2）、Optimism（L2）、Avalanche（高TPS）、BSC（币安链）。HashKey Chain（香港合规专用L2）也是EVM兼容链。',
        pros: '代码直接复用（无需重写）、ERC标准无缝兼容、MetaMask等工具直接支持',
        cons: '各链Gas代币不同、跨链资产须用Bridge、不同链的安全性差异大',
        rwaUse: 'Franklin Templeton选Polygon（Gas低）、BlackRock选以太坊主网（安全性最高）、HashKey选HashKey Chain L2（合规专用）',
      },
    ],
  },
  {
    id: 'oracle',
    icon: 'ri-radar-line',
    color: '#F59E0B',
    title: '预言机：区块链的"现实感知器"',
    subtitle: '将真实世界数据安全地"喂"给链上智能合约',
    metaphor: {
      title: '形象比喻：把"聋子"改造成"顺风耳"',
      content: '区块链本身是个"与世隔绝的聋哑人"——它只知道链上发生了什么，完全不知道今天黄金价格是多少、某个抵押物有没有被法院查封、美联储昨晚有没有加息。\n\n预言机就是给这个聋哑人装上"耳朵和眼睛"——但这个感知器必须可信。这里有个深刻悖论叫"预言机问题"：如果只有一个信息源，喂错了数据怎么办？Chainlink的解法是用几十个独立节点同时报价，取中位数，任何单一节点作弊都会被识别。\n\n2022年Mango Markets被攻击事件说明了预言机的致命重要性：攻击者操纵了预言机价格，让合约以为其代币价值暴涨10倍，然后"合法地"借走了1亿美元。'
    },
    detail: [
      {
        name: '去中心化预言机（Chainlink）',
        tag: '行业标准',
        tagColor: '#F59E0B',
        desc: 'Chainlink是目前规模最大的去中心化预言机网络，通过DON（去中心化预言机网络）聚合多个独立数据源，提供价格、天气、体育比分、随机数等各类链下数据，具有防篡改特性。',
        pros: '多源聚合（防操纵）、高可用性、经过大规模生产验证、支持数百种数据类型',
        cons: '数据更新有延迟（通常1-5分钟）、极端市场波动时可能出现价格滞后',
        rwaUse: 'Ondo Finance OUSG美债代币实时NAV报价、黄金RWA代币实时金价、碳信用价格指数',
      },
      {
        name: 'RWA专用预言机需求',
        tag: 'RWA特定',
        tagColor: '#10B981',
        desc: 'RWA需要特殊类型预言机数据：资产估值（房产、私募基金NAV）、法律状态（资产是否被查封）、合规状态（投资者资质是否仍有效）、利率数据（SOFR利率）。这类数据的链上化仍是RWA最难解决的技术挑战之一。',
        pros: '使链上合约能感知底层资产真实状态，自动触发赎回/清算/分红逻辑',
        cons: '权威数据来源有限、法律状态数据链上化仍在探索阶段',
        rwaUse: 'HKMA Project Ensemble测试用预言机传输底层资产价值，作为代币结算的定价基准',
      },
    ],
  },
  // ... existing bridge, consensus, defi-infra nodes remain
  {
    id: 'bridge',
    icon: 'ri-git-branch-line',
    color: '#10B981',
    title: '两链一桥：跨链互通的核心基础设施',
    subtitle: '解决不同区块链之间资产和数据不互通的关键技术',
    metaphor: {
      title: '形象比喻：货币兑换局 vs 银行电汇——两种完全不同的风险',
      content: '不同区块链就像不同国家的货币：以太坊上的代币无法直接"移植"到Polygon上，就像人民币无法直接在美国消费。\n\n"桥"就是这个汇兑机制。第一种是"锁仓铸造桥"——你把以太坊上的ETH锁在保险柜里，在Polygon上铸造一个等值的"收据"。这个保险柜就是风险核心：Ronin Bridge被盗6.25亿美元、Wormhole被盗3.2亿美元，本质上都是"这个保险柜的钥匙被偷了"。\n\n第二种是"原生桥"——Optimism、Arbitrum这类L2的资产转移通过以太坊主链智能合约保护，安全性由以太坊本身保证。\n\nRWA的"两链一桥"不仅仅是资产桥，更是"法律确权链"（联盟链/境内）与"代币发行链"（公链/境外香港）之间的制度性通道——这个桥的本质是法律承认，而不是技术代码。'
    },
    detail: [
      {
        name: 'RWA"两链一桥"架构',
        tag: 'RWA核心',
        tagColor: '#10B981',
        desc: '"两链"指：①境内联盟链（长安链/蚂蚁链，用于资产确权存证）+ ②境外公链（以太坊/Polygon/HashKey Chain，用于发行可交易代币）。"一桥"指：合法的跨境资产权利转让通道（法律意义上的真实出售+SPV结构+监管批准）。',
        pros: '境内确权合规+境外融资灵活，双链优势互补，满足内地+香港双监管要求',
        cons: '结构复杂（成本高）、两地法律协调难度大、数据同步存在时间差',
        rwaUse: '大多数中国内地资产跨境RWA化的标准路径：内地联盟链存证→合法权利转让→香港SPV→以太坊/HashKey Chain发行',
      },
      {
        name: 'CCIP跨链互操作协议',
        tag: '行业标准',
        tagColor: '#8B5CF6',
        desc: 'CCIP（Chainlink跨链互操作协议）是当前RWA领域最重要的跨链标准，支持代币和消息在不同区块链间安全传输。SWIFT正在与Chainlink CCIP合作测试代币化资产跨链结算。',
        pros: '标准化接口（无需针对每条链单独开发）、安全性由Chainlink节点网络保证',
        cons: '仍处于早期阶段、各链对CCIP的支持程度不一',
        rwaUse: 'SWIFT PoC测试：传统银行通过SWIFT报文触发CCIP指令，完成多链RWA代币转让',
      },
    ],
  },
  {
    id: 'consensus',
    icon: 'ri-team-line',
    color: '#EF4444',
    title: '共识机制：区块链的"民主决策系统"',
    subtitle: 'PoW、PoS、PBFT——不同的"投票规则"决定不同的安全模型',
    metaphor: {
      title: '形象比喻：三种截然不同的"开会表决"方式',
      content: 'PoW（工作量证明）就像"谁的肌肉最大谁说话最响"——比特币节点要抢记账权，必须做大量无用的数学运算（烧电），谁先做完谁赢。坏处：全球挖矿每年耗电量相当于一个中等国家。好处：没有任何人付得起攻击所需的电费。\n\nPoS（权益证明）就像"谁股份最多谁有更多话语权"——质押ETH越多，抽到记账权的概率越大。以太坊2022年从PoW切换到PoS后，能耗下降99.95%。\n\nPBFT（实用拜占庭容错）是联盟链最常用的共识——像一个"小型委员会投票"，只需2/3+1的成员同意即可出块，速度极快但参与者必须是已知可信机构。蚂蚁链、长安链都用这类机制，所以TPS可达万级，但这也意味着它不是"去中心化"的。'
    },
    detail: [
      {
        name: 'PoW vs PoS vs PBFT对比',
        tag: '三种机制',
        tagColor: '#EF4444',
        desc: 'PoW：安全性最高但能耗极大（比特币）；PoS：节能+高安全但有"富者愈富"问题（以太坊）；PBFT及衍生机制：高效+可监管但中心化程度高（联盟链）。RWA在公链侧用PoS（以太坊），在境内侧用PBFT类（联盟链）。',
        pros: '不同机制适配不同场景需求，无单一最优解',
        cons: '没有一种机制能同时满足"安全+去中心化+高效"（区块链不可能三角）',
        rwaUse: 'RWA项目在选链时需考虑共识机制的安全假设：境外代币发行选以太坊（PoS，最高安全性），境内确权选联盟链（PBFT，高效合规）',
      },
    ],
  },
  {
    id: 'defi-infra',
    icon: 'ri-links-line',
    color: '#06B6D4',
    title: 'DeFi基础设施：RWA的"金融乐高"',
    subtitle: '可组合性——为什么RWA代币可以"嵌入"任何DeFi协议',
    metaphor: {
      title: '形象比喻：乐高积木 vs 焊死的玩具',
      content: '传统金融产品像是"焊死的玩具"——你买了一只基金，只能坐等赎回，无法把它当抵押品去银行贷款，更无法把它一秒钟拆成100份卖给不同的人。\n\n区块链上的RWA代币像"乐高积木"——你持有一枚美债RWA代币，可以同时：①抵押给MakerDAO借出DAI稳定币；②放入Uniswap流动性池赚取手续费；③通过Aave出借赚取利息；④拆分为更小单位卖给别人。这四件事可以同时发生。\n\n这种"可组合性"是区块链金融最革命性的特质——也是为什么Blackrock会说"代币化是金融的下一次革命"：实物资产一旦上链，就具备了数字世界的全部可编程能力。'
    },
    detail: [
      {
        name: '智能合约可组合性',
        tag: '核心优势',
        tagColor: '#06B6D4',
        desc: 'DeFi协议之间可以相互调用，RWA代币一旦进入DeFi生态，就可以作为抵押品、流动性来源、收益来源被多个协议同时利用。这是传统证券无法实现的"7×24小时全球可组合"能力。',
        pros: '资本效率极高（一份资产多重利用）、流动性显著提升、用户收益来源多元化',
        cons: '组合越深风险越复杂（"乐高塔"越高越不稳定）、一个环节的漏洞可以导致级联崩溃',
        rwaUse: 'MakerDAO用RWA美债作为DAI的稳定储备；Aave引入RWA作为借贷市场的优质抵押品；Compound类协议测试接受代币化国债',
      },
    ],
  },
];

const ROADMAP_DATA: Record<string, { stage: string; stageColor: string; year: string; maturity: number; milestones: { year: string; event: string; done: boolean }[] }> = {
  'blockchain-types': {
    stage: '成熟期', stageColor: '#10B981', year: '2015-至今', maturity: 90,
    milestones: [
      { year: '2015', event: '以太坊公链上线，智能合约诞生', done: true },
      { year: '2017', event: '联盟链（Hyperledger Fabric）企业级应用', done: true },
      { year: '2020', event: '蚂蚁链、长安链境内大规模部署', done: true },
      { year: '2023', event: 'HashKey Chain香港合规L2上线', done: true },
      { year: '2025', event: '境内外双链RWA架构成为行业标准', done: true },
      { year: '2027', event: '跨链互操作协议统一，多链无缝切换', done: false },
    ],
  },
  'hash': {
    stage: '成熟期', stageColor: '#10B981', year: '2008-至今', maturity: 95,
    milestones: [
      { year: '2008', event: '比特币使用SHA-256，哈希链式结构诞生', done: true },
      { year: '2015', event: '以太坊引入Keccak-256，地址生成标准化', done: true },
      { year: '2020', event: '哈希存证广泛用于合同、版权、供应链', done: true },
      { year: '2024', event: 'RWA合同链上存证成为SFC合规要求', done: true },
      { year: '2026', event: '后量子哈希算法（SHA-3系列）开始迁移', done: false },
    ],
  },
  'merkle': {
    stage: '成熟期', stageColor: '#10B981', year: '2008-至今', maturity: 88,
    milestones: [
      { year: '2008', event: '比特币白皮书引入Merkle Tree', done: true },
      { year: '2020', event: 'Layer2 Rollup大量使用Merkle Proof', done: true },
      { year: '2023', event: 'ZK-Merkle Proof用于隐私RWA验证', done: true },
      { year: '2026', event: 'Verkle Tree替代Merkle Tree，以太坊效率提升', done: false },
    ],
  },
  'keypair': {
    stage: '成熟期', stageColor: '#10B981', year: '2008-至今', maturity: 92,
    milestones: [
      { year: '2008', event: 'ECDSA椭圆曲线签名用于比特币', done: true },
      { year: '2020', event: 'HSM硬件安全模块用于机构私钥托管', done: true },
      { year: '2023', event: 'SFC要求RWA代币私钥独立托管', done: true },
      { year: '2025', event: 'MPC多方计算私钥方案商业化', done: true },
      { year: '2028', event: '后量子签名算法（CRYSTALS-Dilithium）迁移', done: false },
    ],
  },
  'smart-contract': {
    stage: '成长期', stageColor: '#F59E0B', year: '2015-至今', maturity: 75,
    milestones: [
      { year: '2015', event: '以太坊智能合约上线，Solidity语言诞生', done: true },
      { year: '2020', event: 'DeFi Summer，智能合约TVL突破百亿', done: true },
      { year: '2022', event: 'ERC-3643合规代币标准发布', done: true },
      { year: '2024', event: 'SFC要求RWA合约强制安全审计', done: true },
      { year: '2026', event: '形式化验证工具普及，合约安全性大幅提升', done: false },
      { year: '2028', event: 'AI辅助合约审计，漏洞检测率达99%+', done: false },
    ],
  },
  'evm': {
    stage: '成长期', stageColor: '#F59E0B', year: '2015-至今', maturity: 80,
    milestones: [
      { year: '2015', event: 'EVM随以太坊上线，智能合约运行环境标准化', done: true },
      { year: '2020', event: 'Polygon、BSC等EVM兼容链爆发', done: true },
      { year: '2022', event: 'Optimism/Arbitrum L2 EVM兼容方案成熟', done: true },
      { year: '2024', event: 'HashKey Chain（香港合规EVM L2）上线', done: true },
      { year: '2027', event: 'EVM 2.0升级，Gas效率提升10倍', done: false },
    ],
  },
  'oracle': {
    stage: '成长期', stageColor: '#F59E0B', year: '2017-至今', maturity: 70,
    milestones: [
      { year: '2017', event: 'Chainlink主网上线，去中心化预言机诞生', done: true },
      { year: '2021', event: 'Chainlink成为DeFi预言机事实标准', done: true },
      { year: '2023', event: 'HKMA Project Ensemble测试RWA专用预言机', done: true },
      { year: '2025', event: 'SWIFT与Chainlink CCIP完成跨链结算测试', done: true },
      { year: '2027', event: 'RWA资产估值预言机标准化，覆盖主流资产类型', done: false },
      { year: '2030', event: '法律状态预言机（资产查封/解封）实现链上化', done: false },
    ],
  },
  'bridge': {
    stage: '探索期', stageColor: '#EF4444', year: '2020-至今', maturity: 55,
    milestones: [
      { year: '2020', event: '跨链桥兴起，但安全事故频发（Ronin $6.25亿）', done: true },
      { year: '2022', event: 'CCIP跨链互操作协议发布', done: true },
      { year: '2024', event: '境内联盟链+境外公链"两链一桥"架构成型', done: true },
      { year: '2026', event: '监管认可的跨境RWA通道标准化', done: false },
      { year: '2028', event: '跨链安全损失降至接近零，桥接成为基础设施', done: false },
    ],
  },
  'consensus': {
    stage: '成熟期', stageColor: '#10B981', year: '2008-至今', maturity: 85,
    milestones: [
      { year: '2008', event: 'PoW共识随比特币诞生', done: true },
      { year: '2017', event: 'PBFT联盟链共识用于企业区块链', done: true },
      { year: '2022', event: '以太坊完成PoW→PoS合并，能耗降低99.95%', done: true },
      { year: '2025', event: 'PoS+分片技术使以太坊TPS突破10万', done: false },
    ],
  },
  'defi-infra': {
    stage: '成长期', stageColor: '#F59E0B', year: '2020-至今', maturity: 72,
    milestones: [
      { year: '2020', event: 'DeFi Summer，可组合性概念爆发', done: true },
      { year: '2022', event: 'MakerDAO引入RWA作为DAI储备', done: true },
      { year: '2024', event: 'Aave/Compound测试接受代币化国债抵押', done: true },
      { year: '2026', event: 'RWA+DeFi组合产品成为主流机构投资工具', done: false },
      { year: '2028', event: 'RWA DeFi TVL超越原生加密资产DeFi', done: false },
    ],
  },
};

const STAGE_LABELS: Record<string, { label: string; desc: string }> = {
  '成熟期': { label: '成熟期', desc: '技术稳定，大规模商业应用' },
  '成长期': { label: '成长期', desc: '快速发展，标准逐步形成' },
  '探索期': { label: '探索期', desc: '早期阶段，仍有重大挑战' },
};

export default function BlockchainTechSection() {
  const [activeNode, setActiveNode] = useState<string>('blockchain-types');
  const [showRoadmap, setShowRoadmap] = useState(false);
  const { t } = useLang();
  const current = TECH_NODES.find((n) => n.id === activeNode)!;
  const roadmap = ROADMAP_DATA[activeNode];

  return (
    <section id="blockchain-tech" className="py-20 bg-white dark:bg-gray-900">
      <div className="max-w-[1440px] mx-auto px-4 lg:px-8">
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#7C3AED]/10 dark:bg-[#7C3AED]/20 rounded-full mb-4">
            <i className="ri-settings-3-line text-[#7C3AED] text-sm"></i>
            <span className="text-[#7C3AED] text-sm font-semibold">{t('12 · 区块链技术架构深度解析', '12 · Blockchain Architecture Deep Dive')}</span>
          </div>
          <h2 className="text-3xl lg:text-4xl font-black text-gray-900 dark:text-white mb-4">{t('区块链技术架构：每个节点的意义', 'Blockchain Architecture: The Meaning of Every Node')}</h2>
          <p className="text-gray-500 dark:text-gray-400 max-w-3xl mx-auto text-base leading-relaxed">
            {t(
              '哈希值、默克尔树、私钥/公钥、智能合约、EVM、联盟链、跨链桥……每一个技术节点在RWA链上发行中都有不可替代的作用。通过深度比喻与技术精讲，帮助你真正理解区块链架构，而不只是记住名词。',
              'Hash values, Merkle trees, private/public keys, smart contracts, EVM, consortium chains, cross-chain bridges... every technical node plays an irreplaceable role in RWA on-chain issuance. Through deep analogies and technical explanations, truly understand blockchain architecture — not just memorize terms.'
            )}
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          <div className="lg:w-72 shrink-0">
            <div className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-3 space-y-1 sticky top-6">
              <div className="text-xs font-bold text-gray-400 uppercase tracking-wider px-3 py-2">{t('技术节点导航', 'Tech Node Navigation')}</div>
              {TECH_NODES.map((node) => (
                <button
                  key={node.id}
                  onClick={() => setActiveNode(node.id)}
                  className={`w-full text-left flex items-center gap-3 px-3 py-3 rounded-xl transition-all cursor-pointer ${
                    activeNode === node.id ? 'bg-white dark:bg-gray-700 shadow-sm' : 'hover:bg-white/60 dark:hover:bg-gray-700/60'
                  }`}
                >
                  <div className={`w-8 h-8 flex items-center justify-center rounded-lg shrink-0 ${activeNode !== node.id ? 'bg-gray-100 dark:bg-gray-600' : ''}`}>
                    <i className={`${node.icon} text-sm`} style={{ color: activeNode === node.id ? node.color : '' }} className={activeNode !== node.id ? 'text-gray-400' : ''}></i>
                  </div>
                  <div className="min-w-0">
                    <div className={`text-sm font-semibold leading-none mb-0.5 truncate ${activeNode === node.id ? 'text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-400'}`}>{node.title.split('：')[0]}</div>
                    <div className="text-xs text-gray-400 truncate">{node.subtitle}</div>
                  </div>
                  {activeNode === node.id && (
                    <div className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: node.color }}></div>
                  )}
                </button>
              ))}
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="mb-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl" style={{ background: `${current.color}15` }}>
                  <i className={`${current.icon} text-lg`} style={{ color: current.color }}></i>
                </div>
                <div>
                  <h3 className="text-xl font-black text-gray-900 dark:text-white">{current.title}</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{current.subtitle}</p>
                </div>
              </div>
            </div>

            <div className="mb-6 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border border-amber-200/60 dark:border-amber-700/40 rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-6 h-6 flex items-center justify-center bg-amber-100 dark:bg-amber-800/40 rounded-lg">
                  <i className="ri-lightbulb-line text-amber-600 text-sm"></i>
                </div>
                <span className="text-sm font-bold text-amber-800 dark:text-amber-300">{current.metaphor.title}</span>
              </div>
              <p className="text-amber-900 dark:text-amber-200 text-sm leading-relaxed whitespace-pre-line">{current.metaphor.content}</p>
            </div>

            <div className="space-y-4 mb-6">
              {current.detail.map((item, i) => (
                <div key={i} className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-5 border border-gray-100 dark:border-gray-700">
                  <div className="flex items-center gap-3 mb-3">
                    <h4 className="font-bold text-gray-900 dark:text-white text-base">{item.name}</h4>
                    <span className="text-xs px-2.5 py-0.5 rounded-full font-semibold text-white" style={{ background: item.tagColor }}>{item.tag}</span>
                  </div>
                  <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed mb-4">{item.desc}</p>
                  <div className="grid md:grid-cols-3 gap-3">
                    <div className="bg-green-50 dark:bg-green-900/20 border border-green-100 dark:border-green-800/40 rounded-xl p-3">
                      <div className="text-xs font-bold text-green-700 dark:text-green-400 mb-1.5 flex items-center gap-1">
                        <i className="ri-thumb-up-line"></i>{t('核心优势', 'Key Advantages')}
                      </div>
                      <p className="text-xs text-green-800 dark:text-green-300 leading-relaxed">{item.pros}</p>
                    </div>
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800/40 rounded-xl p-3">
                      <div className="text-xs font-bold text-red-700 dark:text-red-400 mb-1.5 flex items-center gap-1">
                        <i className="ri-alert-line"></i>{t('主要局限', 'Main Limitations')}
                      </div>
                      <p className="text-xs text-red-800 dark:text-red-300 leading-relaxed">{item.cons}</p>
                    </div>
                    <div className="rounded-xl p-3 border" style={{ background: `${current.color}08`, borderColor: `${current.color}20` }}>
                      <div className="text-xs font-bold mb-1.5 flex items-center gap-1" style={{ color: current.color }}>
                        <i className="ri-building-line"></i>{t('RWA应用', 'RWA Application')}
                      </div>
                      <p className="text-xs leading-relaxed" style={{ color: `${current.color}CC` }}>{item.rwaUse}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {current.chinaNote && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200/60 dark:border-red-800/40 rounded-2xl p-6 mb-6">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-6 h-6 flex items-center justify-center bg-red-100 dark:bg-red-800/40 rounded-lg">
                    <i className="ri-government-line text-red-600 text-sm"></i>
                  </div>
                  <span className="text-sm font-bold text-red-800 dark:text-red-300">{current.chinaNote.title}</span>
                </div>
                <div className="space-y-3">
                  {current.chinaNote.content.map((item, i) => (
                    <div key={i} className="flex gap-3">
                      <div className="w-5 h-5 flex items-center justify-center bg-red-100 dark:bg-red-800/40 rounded-full shrink-0 mt-0.5">
                        <span className="text-red-600 dark:text-red-400 text-xs font-bold">{i + 1}</span>
                      </div>
                      <p className="text-red-900 dark:text-red-200 text-sm leading-relaxed">{item}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Tech Roadmap Toggle */}
            {roadmap && (
              <div className="border border-gray-100 dark:border-gray-700 rounded-2xl overflow-hidden">
                <button
                  onClick={() => setShowRoadmap(!showRoadmap)}
                  className="w-full flex items-center justify-between px-5 py-4 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-7 h-7 flex items-center justify-center rounded-lg" style={{ background: `${current.color}15` }}>
                      <i className="ri-road-map-line text-sm" style={{ color: current.color }}></i>
                    </div>
                    <span className="font-bold text-gray-900 dark:text-white text-sm">技术路线图 · 发展阶段与成熟度</span>
                    <span className="text-xs px-2 py-0.5 rounded-full font-bold text-white" style={{ background: roadmap.stageColor }}>
                      {roadmap.stage}
                    </span>
                  </div>
                  {showRoadmap ? <i className="ri-arrow-up-s-line text-gray-400"></i> : <i className="ri-arrow-down-s-line text-gray-400"></i>}
                </button>

                {showRoadmap && (
                  <div className="p-5 bg-white dark:bg-gray-900">
                    {/* Maturity Bar */}
                    <div className="mb-6">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-bold text-gray-600 dark:text-gray-400">技术成熟度</span>
                        <span className="text-xs font-black" style={{ color: current.color }}>{roadmap.maturity}%</span>
                      </div>
                      <div className="h-2.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all duration-700"
                          style={{ width: `${roadmap.maturity}%`, background: `linear-gradient(90deg, ${current.color}88, ${current.color})` }}
                        ></div>
                      </div>
                      <div className="flex justify-between mt-1.5">
                        {['探索期', '成长期', '成熟期', '普及期'].map((s, idx) => (
                          <span key={s} className="text-xs text-gray-400" style={{ opacity: roadmap.maturity >= (idx + 1) * 25 ? 1 : 0.4 }}>{s}</span>
                        ))}
                      </div>
                    </div>

                    {/* Stage Info */}
                    <div className="mb-5 p-3 rounded-xl border" style={{ background: `${roadmap.stageColor}08`, borderColor: `${roadmap.stageColor}20` }}>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full" style={{ background: roadmap.stageColor }}></div>
                        <span className="text-xs font-bold" style={{ color: roadmap.stageColor }}>{STAGE_LABELS[roadmap.stage]?.label}</span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">— {STAGE_LABELS[roadmap.stage]?.desc}</span>
                        <span className="ml-auto text-xs text-gray-400">{roadmap.year}</span>
                      </div>
                    </div>

                    {/* Milestones Timeline */}
                    <div className="relative">
                      <div className="absolute left-3.5 top-0 bottom-0 w-px bg-gray-100 dark:bg-gray-700"></div>
                      <div className="space-y-4">
                        {roadmap.milestones.map((m, idx) => (
                          <div key={idx} className="flex items-start gap-4 pl-10 relative">
                            <div
                              className="absolute left-0 w-7 h-7 rounded-full flex items-center justify-center border-2 border-white dark:border-gray-900 shrink-0"
                              style={{ background: m.done ? current.color : '#E5E7EB', top: '0px' }}
                            >
                              <i className={`${m.done ? 'ri-check-line' : 'ri-time-line'} text-xs text-white`}></i>
                            </div>
                            <div className="flex-1 pb-1">
                              <div className="flex items-center gap-2 mb-0.5">
                                <span className="text-xs font-black px-2 py-0.5 rounded-md" style={{ background: m.done ? `${current.color}15` : '#F3F4F6', color: m.done ? current.color : '#9CA3AF' }}>
                                  {m.year}
                                </span>
                                {!m.done && <span className="text-xs text-gray-400 italic">预测</span>}
                              </div>
                              <p className={`text-sm leading-relaxed ${m.done ? 'text-gray-700 dark:text-gray-300' : 'text-gray-400'}`}>{m.event}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
